import { appendModalControllerIfRequired } from '../../global/helpers';
import { nextByCond } from '../../global/ui-helpers';
import { eventBus } from '../../global/index';
import { browser } from '../../global/browser';
import { Labels } from '../../global/labels';
import { createElement, updateElement } from '../../global/render-api';
import { element, html } from '../../global/template-literals';
import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import type { UITable } from '../../components/table/ui-table';
import type {
    AttributeValue,
    ElementConfig,
    ObservedAttributes,
} from '../ui-element.type';
import type { TranslationLabels } from '../../types/labels.type';
import type { NullOr } from '../../global/utils.type';
import type {
    UIComparisonCardsLayout,
    UIComparisonCardsLayouts,
    UIComparisonCardsComparisonValue,
} from './ui-comparison-cards.type';
import type { UIColumns } from '../columns/ui-columns';
import type { UIIcon } from '../icon/ui-icon';
import type { UIColumn } from '../columns/ui-column';
import type { UIModal } from '../modal/ui-modal';
import styles from './ui-comparison-cards.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIComparisonCards
 * @element ui-comparison-cards
 * @classdesc Represents a class for <code>ui-comparison-cards</code> element.
 * @property {("table" | "cards")} [layout="table"] {@attr layout} Current layout,
 * shouldn't be changed manually.
 * @property {string} [figcaptionSelectors] {@attr figcaption-selectors}
 * Comma separated list of selectors which elements inside "figcaption"
 * should be visible additionally in modal comparison view.
 * @property {Element} comparisonModalContent Reference to comparison modal content
 * @property {HTMLTemplateElement} template ui-table template
 * @example
 * <ui-comparison-cards>
 *   <template>
 *     <ui-table></ui-table>
 *   </template>
 * </ui-comparison-cards>
 */
@Component({ tag: 'ui-comparison-cards', styles })
export class UIComparisonCards extends UIElement {
    static readonly Layout: UIComparisonCardsLayouts = {
        Table: 'table',
        Cards: 'cards',
    };

    @Attribute(String) layout: UIComparisonCardsLayout =
        UIComparisonCards.Layout.Table;
    @Attribute(String) figcaptionSelectors?: string;

    @Child('template') template?: HTMLTemplateElement;

    /** @private */
    private previousComparisonValueA: NullOr<string | number> = null;
    /** @private */
    private previousComparisonValueB: NullOr<string | number> = null;
    /** @private */
    comparisonModalContent: NullOr<HTMLElement> = null;
    /** @private */
    private readonly changeLayoutHandler = this.changeLayout.bind(this);

    static get observedAttributes(): ObservedAttributes {
        return ['layout'];
    }

    /**
     * Localization
     * @type {Labels}
     * @readonly
     */
    static get labels(): TranslationLabels.Labels {
        return Labels.attach('ui-comparison-cards', {
            compareDetails: 'Compare details',
            available: 'Available',
            notAvailable: 'Not available',
            comparisonItem1: 'Comparison item 1',
            comparisonItem2: 'Comparison item 2',
            selectProductsCompare: 'Select which products to compare.',
        });
    }

    /**
     * Returns true for small screens
     * @returns {boolean}
     */
    get isSmallScreen(): boolean {
        return browser.mobileMediaMatches();
    }

    /**
     * Gets table from template element
     * @returns {UITable}
     */
    get tableTemplate(): UITable {
        let template = this.template;
        if (!template) {
            if (
                (
                    this.constructor as typeof UIComparisonCards & {
                        DEBUG_MODE?: boolean;
                    }
                ).DEBUG_MODE
            ) {
                console.warn(`'<template></template>' is missing`, this);
            }
            template = createElement<HTMLTemplateElement>({
                tagName: 'template',
            })!;
            updateElement(template.content as unknown as HTMLElement, {
                children: [
                    {
                        tagName: 'ui-table',
                    },
                ],
            });
        }

        let uiTable: NullOr<UITable> =
            (template.content
                .querySelector<UITable>('ui-table')
                ?.cloneNode(true) as UITable) ?? null;
        if (!uiTable) {
            uiTable = createElement<UITable>({ tagName: 'ui-table' })!;
        }

        let table = uiTable.querySelector<HTMLTableElement>('table');
        if (!table) {
            table = createElement<HTMLTableElement>({ tagName: 'table' })!;
            updateElement(uiTable, {
                children: [table],
            });
        }

        if (!table.querySelector('thead')) {
            updateElement(table, {
                children: [
                    {
                        tagName: 'thead',
                    },
                ],
            });
        }

        if (!table.querySelector('tbody')) {
            updateElement(table, {
                children: [
                    {
                        tagName: 'tbody',
                    },
                ],
            });
        }

        if (!table.querySelector('tfoot')) {
            updateElement(table, {
                children: [
                    {
                        tagName: 'tfoot',
                    },
                ],
            });
        }

        // hide all rows with "-featured" class that don't have a th
        table
            .querySelectorAll<HTMLTableRowElement>('tr.-featured:not(.-hidden)')
            .forEach((row) => {
                if (!row.querySelector(':scope > th')) {
                    row.classList.add('-hidden');
                }
            });

        const plugins = (uiTable.getAttribute('plugins') || '')
            .split(' ')
            .filter((x) => x);
        const missingPlugins = ['comparison', 'auto'].filter(
            (plugin) => !plugins.includes(plugin)
        );
        if (missingPlugins.length) {
            uiTable.setAttribute(
                'plugins',
                [...plugins, ...missingPlugins].join(' ')
            );
        }

        return uiTable;
    }

    /**
     * Gets next non-hidden cell index.
     * @private
     * @param {number} index
     * @returns {number}
     */
    private getNextComparedIndex(index: number): number {
        const table =
            this.tableTemplate.querySelector<HTMLTableElement>('table');
        const headerCells =
            table?.tHead?.querySelectorAll<HTMLTableCellElement>(
                'th[scope="col"]'
            );
        if (!headerCells) {
            return -1;
        }
        const current = headerCells[index];
        const next = nextByCond(current, (cell) => {
            return (
                !cell.classList.contains('-hidden') &&
                cell.getAttribute('scope') !== 'row'
            );
        });
        return [].findIndex.call(
            headerCells,
            (cell: HTMLTableCellElement) => cell === next
        );
    }

    get comparisonModalSelects(): NodeListOf<HTMLSelectElement> {
        return (
            this.comparisonModalContent?.querySelectorAll<HTMLSelectElement>(
                '.ui-comparison-cards__comparison-select'
            ) ?? ([] as unknown as NodeListOf<HTMLSelectElement>)
        );
    }

    get comparisonSelectA(): HTMLSelectElement {
        return this.comparisonModalSelects[0];
    }

    get comparisonSelectB(): HTMLSelectElement {
        return this.comparisonModalSelects[1];
    }

    get comparisonValueA(): number {
        return +this.comparisonSelectA.value;
    }

    get comparisonValueB(): number {
        return +this.comparisonSelectB.value;
    }

    get comparisonModal(): NullOr<UIModal> {
        return (
            this.comparisonModalContent?.closest<UIModal>('ui-modal') ?? null
        );
    }

    /**
     * Constructs feature element for smaller screens
     * @param {HTMLTableRowElement} row
     * @param {number} index
     * @returns {DocumentFragment | ElementConfig | null}
     */
    constructFeatureListItem(
        row: HTMLTableRowElement,
        index: number
    ): DocumentFragment | ElementConfig | null {
        const header =
            row.querySelector<HTMLTableCellElement>('th[scope="row"]');
        const cell = row.querySelectorAll<HTMLTableCellElement>('td')[index];

        if (!cell) {
            console.error(`Missing <td></td> for column: ${index}`, row, this);
            return null;
        }

        // only show available features
        if (cell.querySelector('ui-icon[glyph="cross"]')) {
            return null;
        }

        const getHeaderChildren = (): Array<DocumentFragment> =>
            [].map.call(
                header?.childNodes,
                (child: Node) => html`${child}`
            ) as Array<DocumentFragment>;

        /**
         * @param {{lowercaseFirstChar: boolean}} [opts]
         * @returns {Array<DocumentFragment | string | null>}
         */
        const getCellChildren = ({
            lowercaseFirstChar = false,
        }: { lowercaseFirstChar?: boolean } = {}): Array<
            NullOr<DocumentFragment | string>
        > =>
            [].map.call(cell.childNodes, (child: Node) => {
                const node = child.cloneNode(true);
                if (node.nodeType === Node.TEXT_NODE) {
                    const text = (node as Text).textContent?.trim() ?? '';
                    if (!text) {
                        return null;
                    }
                    if (lowercaseFirstChar) {
                        return text.charAt(0).toLowerCase() + text.slice(1);
                    }
                    return text;
                }

                return html`${node}`;
            }) as Array<NullOr<DocumentFragment | string>>;

        const getFeatureText = (): DocumentFragment => {
            if (header?.childNodes.length) {
                if (cell.textContent?.trim()) {
                    return html`${getHeaderChildren()}
                    ${getCellChildren({ lowercaseFirstChar: true })}`;
                }
                return html`${getHeaderChildren()}`;
            }

            return html`${getCellChildren()}`;
        };

        const featureText = getFeatureText();
        return featureText.childNodes.length
            ? { tagName: 'li', children: [featureText] }
            : null;
    }

    /**
     * Creates cards layout
     * @returns {HTMLElement}
     */
    createCardsLayout(): UIColumns {
        const table =
            this.tableTemplate.querySelector<HTMLTableElement>('table')!;
        // A table can have either a single row of th cells with rich content, or two rows, one with ths that contain column labels and one with tds that contain rich content. The latter is to enhance accessibility by providing a simple readable label to each column.
        const richHeaderCells =
            table.tHead!.querySelectorAll<HTMLTableCellElement>(
                'tr:only-child th[scope="col"], tr:first-child:has(th) + tr td:not(:first-child)'
            );
        const featuredRows =
            table.tBodies[0].querySelectorAll<HTMLTableRowElement>(
                'tr.-featured'
            );

        return createElement<UIColumns>({
            tagName: 'ui-columns',
            classList: {
                'ui-comparison-cards__layout-cards': true,
            },
            attributes: {
                role: 'group',
            },
            children: Array.from<HTMLTableCellElement>(richHeaderCells).map(
                (
                    cell: HTMLTableCellElement,
                    i: number
                ): NullOr<HTMLElement> => {
                    const clonedHeaderCell = cell.cloneNode(
                        true
                    ) as HTMLTableCellElement;
                    const header =
                        clonedHeaderCell.querySelector<HTMLHeadingElement>(
                            ':is(h1, h2, h3, h4, h5, h6)'
                        );
                    const descriptions =
                        clonedHeaderCell.querySelectorAll<HTMLParagraphElement>(
                            'p'
                        );
                    const img =
                        clonedHeaderCell.querySelector<HTMLImageElement>('img');
                    const mainBtn =
                        clonedHeaderCell.querySelector<HTMLButtonElement>(
                            'button.button[type="button"]'
                        );
                    const moreDetailsLink =
                        clonedHeaderCell.querySelector<HTMLAnchorElement>(
                            'a.default-link,ui-link[layout="primary"]'
                        );
                    const filteredElements = [mainBtn, moreDetailsLink];
                    const descriptionsFiltered = [].filter
                        .call(
                            descriptions,
                            (description: HTMLParagraphElement) =>
                                ![].some.call(
                                    description.children,
                                    (child: HTMLButtonElement) =>
                                        filteredElements.includes(child)
                                )
                        )
                        .map((description: HTMLParagraphElement) => {
                            return html`${description}`;
                        });
                    const compareBtn: ElementConfig = {
                        tagName: 'button',
                        classList: { 'button': true, '-guiding': true },
                        attributes: {
                            type: 'button',
                        },
                        events: {
                            click: () => this.openComparisonModal(i),
                        },
                        children: [UIComparisonCards.labels.compareDetails],
                    };

                    const features: Array<
                        DocumentFragment | ElementConfig | null
                    > = [];
                    featuredRows.forEach((row: HTMLTableRowElement) => {
                        if (row.querySelector('td[colspan]')) {
                            console.error(
                                '-featured class rows should not have colspan',
                                row
                            );
                        } else {
                            const clonedRow = row.cloneNode(
                                true
                            ) as HTMLTableRowElement;
                            features.push(
                                this.constructFeatureListItem(clonedRow, i)
                            );
                        }
                    });

                    const clonedClass =
                        clonedHeaderCell.getAttribute('class') || null;
                    const clonedId =
                        clonedHeaderCell.getAttribute('id') || null;
                    return createElement<UIColumn>({
                        tagName: 'ui-column',
                        classList: {
                            [clonedClass ?? '']: !!clonedClass,
                        },
                        attributes: {
                            id: clonedId,
                        },
                        children: [
                            {
                                tagName: 'ui-card',
                                children: [
                                    html`
                                        <article>
                                            <ui-placement
                                                class="ui-comparison-cards__heading"
                                                alignment="center"
                                                spacious
                                            >
                                                ${img} ${header}
                                                ${descriptionsFiltered}
                                            </ui-placement>
                                            ${features.length > 0 &&
                                            html`
                                                <ul
                                                    class="feature-list ui-comparison-cards__features"
                                                >
                                                    ${features.map(
                                                        (feature) =>
                                                            html`${feature}`
                                                    )}
                                                </ul>
                                            `}
                                            <ui-buttonbar>
                                                ${mainBtn} ${compareBtn}
                                            </ui-buttonbar>
                                            <ui-placement
                                                class="ui-comparison-cards__more-details"
                                                alignment="center"
                                            >
                                                ${moreDetailsLink}
                                            </ui-placement>
                                        </article>
                                    `,
                                ],
                            },
                        ],
                    });
                }
            ),
        })!;
    }

    /**
     * Creates table layout
     * @returns {HTMLElement}
     */
    createTableLayout(): UITable {
        const table = this.tableTemplate;
        const availableIcons = table.querySelectorAll<UIIcon>(
            'ui-icon[glyph="agree"][bgcolor="orange"]'
        );
        const unavailableIcons = table.querySelectorAll<UIIcon>(
            'ui-icon[glyph="cross"][bgcolor="bark-30"]'
        );
        availableIcons.forEach((icon) => {
            icon.ariaLabel =
                icon.ariaLabel || String(UIComparisonCards.labels.available);
        });
        unavailableIcons.forEach((icon) => {
            icon.ariaLabel =
                icon.ariaLabel || String(UIComparisonCards.labels.notAvailable);
        });
        table.classList.add('ui-comparison-cards__layout-table');
        return table;
    }

    /**
     * Changes layout based on screen size
     */
    changeLayout(): void {
        this.layout = this.isSmallScreen
            ? UIComparisonCards.Layout.Cards
            : UIComparisonCards.Layout.Table;
        if (this.comparisonModalContent) {
            this.closeComparisonModal();
        }
    }

    /**
     * Creates comparison table
     * @param {number} comparisonIndexA
     * @param {number} comparisonIndexB
     * @returns {HTMLElement}
     */
    createComparisonTable(
        comparisonIndexA: number,
        comparisonIndexB: number
    ): UITable {
        const uiTable = this.tableTemplate;
        const table = uiTable.querySelector<HTMLTableElement>('table')!;
        table.querySelector('colgroup')?.remove();

        const removeIrrelevantCells = (
            cells: NodeListOf<HTMLTableCellElement>
        ): void => {
            cells.forEach((cell, index) => {
                if (index !== comparisonIndexA && index !== comparisonIndexB) {
                    cell.remove();
                }
            });
        };

        const swapCellsPosition = (
            cells: NodeListOf<HTMLTableCellElement>
        ): void => {
            const firstCell = cells[0];
            firstCell.parentNode?.appendChild(firstCell);
        };

        const removeHiddenRows = (
            rows: HTMLCollectionOf<HTMLTableRowElement>
        ): void => {
            const hiddenRows = [].filter.call(
                rows,
                (row: HTMLTableRowElement) => row.classList.contains('-hidden')
            ) as Array<HTMLTableRowElement>;
            hiddenRows.forEach((row) => row.remove());
        };

        const tHeadRows = table.tHead!.rows;
        [].forEach.call(tHeadRows, (row: HTMLTableRowElement) => {
            const cells = row.querySelectorAll<HTMLTableCellElement>(
                ':is(th, td):not(:first-child)'
            );
            removeIrrelevantCells(cells);
            cells.forEach((cell) => {
                const allowedSelectors = `h3, ui-badge, ${this.figcaptionSelectors}`;
                const childrenToRemove = cell.querySelectorAll<HTMLElement>(
                    `figcaption > :not(:where(${allowedSelectors}))`
                );
                childrenToRemove.forEach((child) => child.remove());
            });
        });

        const bodyRows = table.tBodies[0].rows;
        removeHiddenRows(bodyRows);
        [].forEach.call(bodyRows, (row: HTMLTableRowElement) => {
            const cells = row.querySelectorAll<HTMLTableCellElement>('td');
            removeIrrelevantCells(cells);
        });

        const footerRows = table.tFoot!.rows;
        [].forEach.call(footerRows, (row: HTMLTableRowElement) => {
            const cells = row.querySelectorAll<HTMLTableCellElement>('td');
            removeIrrelevantCells(cells);
        });

        // reverse order of rows
        if (comparisonIndexA > comparisonIndexB) {
            [].forEach.call(table.tHead!.rows, (row: HTMLTableRowElement) => {
                const cells = row.querySelectorAll<HTMLTableCellElement>(
                    ':is(th, td):not(:first-child)'
                );
                swapCellsPosition(cells);
            });

            const bodyRows2 = table.tBodies[0].rows;
            [].forEach.call(bodyRows2, (row: HTMLTableRowElement) => {
                const cells = row.querySelectorAll<HTMLTableCellElement>('td');
                swapCellsPosition(cells);
            });

            const footerRows2 = table.tFoot!.rows;
            [].forEach.call(footerRows2, (row: HTMLTableRowElement) => {
                const cells = row.querySelectorAll<HTMLTableCellElement>('td');
                swapCellsPosition(cells);
            });
        }

        return uiTable;
    }

    /**
     * Creates comparison select
     * @param {number} comparisonIndexA
     * @param {number} comparisonIndexB
     * @returns {ElementConfig} - ui-field config
     */
    createComparisonSelect(
        comparisonIndexA: number,
        comparisonIndexB: number
    ): ElementConfig {
        const table =
            this.tableTemplate.querySelector<HTMLTableElement>('table');
        const headerCells =
            table?.tHead?.querySelectorAll<HTMLTableCellElement>(
                'tr:only-child th[scope="col"], tr:first-child:has(th) + tr td:not(:first-child)'
            );
        const values: Array<UIComparisonCardsComparisonValue> = [];

        if (headerCells !== undefined) {
            for (let i = 0; i < headerCells.length; i++) {
                if (headerCells[i].classList.contains('-hidden')) {
                    continue;
                }
                const cell = headerCells[i];
                values.push({
                    index: i,
                    content:
                        cell.querySelector<HTMLElement>(
                            ':is(h1, h2, h3, h4, h5, h6, p)'
                        )?.textContent ||
                        cell.textContent ||
                        '',
                });
            }
        }

        const onSelectAChange = (e?: Event | CustomEvent): void => {
            const valueA = +(e?.target as HTMLSelectElement).value;
            let valueB = this.comparisonValueB;
            if (valueA === valueB) {
                valueB = Number(this.previousComparisonValueA);
                this.comparisonSelectB.value = String(valueB);
            }
            this.changeComparison(valueA, valueB);
        };
        const onSelectBChange = (e?: Event | CustomEvent): void => {
            let valueA = this.comparisonValueA;
            const valueB = +(e?.target as HTMLSelectElement).value;
            if (valueA === valueB) {
                valueA = Number(this.previousComparisonValueB);
                this.comparisonSelectA.value = String(valueA);
            }
            this.changeComparison(valueA, valueB);
        };
        return {
            tagName: 'ui-field',
            children: [
                {
                    tagName: 'select',
                    attributes: {
                        'aria-label': String(
                            UIComparisonCards.labels.comparisonItem1
                        ),
                    },
                    classList: {
                        'ui-comparison-cards__comparison-select': true,
                    },
                    events: {
                        change: onSelectAChange,
                    },
                    children: values.map((option) => {
                        return {
                            tagName: 'option',
                            attributes: {
                                value: option.index,
                                selected: option.index === comparisonIndexA,
                            },
                            children: [option.content],
                        };
                    }),
                },
                {
                    tagName: 'select',
                    attributes: {
                        'aria-label': String(
                            UIComparisonCards.labels.comparisonItem2
                        ),
                    },
                    classList: {
                        'ui-comparison-cards__comparison-select': true,
                    },
                    events: {
                        change: onSelectBChange,
                    },
                    children: values.map((option) => {
                        return {
                            tagName: 'option',
                            attributes: {
                                value: option.index,
                                selected: option.index === comparisonIndexB,
                            },
                            children: [option.content],
                        };
                    }),
                },
            ],
        };
    }

    /**
     * Creates comparison select
     * @param {number} cellIndex
     */
    openComparisonModal(cellIndex: number): void {
        let comparedWithIndex = this.getNextComparedIndex(cellIndex);
        if (comparedWithIndex === -1) {
            comparedWithIndex = cellIndex === 0 ? 1 : 0;
        }
        const select = this.createComparisonSelect(
            cellIndex,
            comparedWithIndex
        );
        const table = this.createComparisonTable(cellIndex, comparedWithIndex);
        this.comparisonModalContent = element`
            <div class="ui-comparison-cards__modal-content">
              <h2>${UIComparisonCards.labels.compareDetails}</h2>
              <p>${UIComparisonCards.labels.selectProductsCompare}</p>
              <div class="ui-comparison-cards__modal-content__select">
                ${select}
              </div>
              ${table}
            </div>
          `;
        eventBus.dispatchCustomEvent('modal-open', {
            content: this.comparisonModalContent,
            params: {
                classList: {
                    'ui-comparison-cards__modal': true,
                },
                onClose: () => {
                    this.comparisonModalContent = null;
                },
            },
        });
        this.previousComparisonValueA = this.comparisonValueA;
        this.previousComparisonValueB = this.comparisonValueB;
    }

    /**
     * Closes comparison modal if it's open
     */
    closeComparisonModal(): void {
        this.comparisonModal?.close?.();
        this.comparisonModalContent = null;
    }

    /**
     * Swaps comparison table with different values
     * @param {number} comparisonIndexA
     * @param {number} comparisonIndexB
     */
    changeComparison(comparisonIndexA: number, comparisonIndexB: number): void {
        const table = this.createComparisonTable(
            comparisonIndexA,
            comparisonIndexB
        );
        const previousTable =
            this.comparisonModalContent?.querySelector<UITable>('ui-table');

        previousTable?.replaceWith(table);
        this.previousComparisonValueA = comparisonIndexA;
        this.previousComparisonValueB = comparisonIndexB;
    }

    /**
     * Renders element layout based on layout attribute
     * @returns {HTMLElement}
     */
    renderLayout(): UITable | UIColumns {
        switch (this.layout) {
            case UIComparisonCards.Layout.Cards:
                return this.createCardsLayout();
            case UIComparisonCards.Layout.Table:
            default:
                return this.createTableLayout();
        }
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: NullOr<string>
    ): void {
        if (!oldValue) {
            return;
        }

        switch (name) {
            case 'layout': {
                const previousLayout = this.querySelector<HTMLElement>(
                    `.ui-comparison-cards__layout-${oldValue}`
                );
                if (previousLayout) {
                    const layout = this.renderLayout();
                    this.replaceChild(layout, previousLayout);
                }
                break;
            }
            /* istanbul ignore next */
            default:
                break;
        }
    }

    /** @private */
    private subscribeEvents(): void {
        browser.mobileMedia.addEventListener(
            'change',
            this.changeLayoutHandler
        );
    }

    /** @private */
    private unsubscribeEvents(): void {
        browser.mobileMedia.removeEventListener(
            'change',
            this.changeLayoutHandler
        );
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.setAttribute('layout', this.layout);
        const layout = this.renderLayout();
        this.insertBefore(layout, this.template ?? null);
    }

    /**
     * @inheritDoc
     */
    override hydrate(): void {
        this.subscribeEvents();
        this.changeLayout();
        appendModalControllerIfRequired();
    }

    /**
     * @inheritDoc
     */
    override disconnect(): void {
        this.unsubscribeEvents();
    }

    /**
     * @inheritDoc
     */
    override reconnect(): void {
        this.subscribeEvents();
        this.changeLayout();
    }
}
